import { PaymentPlanSummary } from "@/components/payment/PaymentPlanSummary";
import { useCancelSubscription } from "@/hooks/api/payment/useCancelSubscription";
import { useSubscriptionCheckout } from "@/hooks/api/payment/useSubscriptionCheckout";
import { useQuery } from "@tanstack/react-query";
import { paymentInfoQueryOptions } from "@vixi/api";
import { RouteOptionsById, RouterIds } from "@vixi/router";
import { PaymentPlan, SubscriptionType } from "@vixi/types";
import { FilterTabs, LoadingScreen, PageMainContent } from "@vixi/ui";
import { assert } from "@vixi/utils";
import { useEffect, useMemo, useState } from "react";

// Constants
const routeId = "/_app/premium/plans" satisfies RouterIds;

export const PlansPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
};

function RouteComponent() {
  // useGoBack(null); used in the parent route
  const [selectedSubscriptionTypeId, setSelectedSubscriptionTypeId] = useState<
    SubscriptionType["id"] | null
  >(null);

  const { data, isSuccess } = useQuery(paymentInfoQueryOptions());
  const { subscriptionTypes: maybeSubscriptionTypes, planTypes } = data ?? {};

  // Set the default selected subscription type when data is fetched
  useEffect(() => {
    if (isSuccess) {
      const { subscriptionTypes } = data;
      setSelectedSubscriptionTypeId(subscriptionTypes[0]?.id);
    }
  }, [isSuccess]);

  // Get the selected subscription type based on the selectedSubscriptionTypeId
  const selectedSubscriptionType = useMemo(() => {
    return maybeSubscriptionTypes?.find((sub) => sub.id === selectedSubscriptionTypeId);
  }, [selectedSubscriptionTypeId, maybeSubscriptionTypes]);

  // Create subscription checkout
  const {
    mutateAsync: createCheckout,
    isPending: isCreatingCheckoutSession,
    data: _checkoutData,
  } = useSubscriptionCheckout();
  const {
    mutateAsync: cancelSubscription,
    isPending: isCancelingSubscription,
    data: _cancelSubscriptionData,
  } = useCancelSubscription();
  const onSwitchPlan = async (plan: PaymentPlan, isFree: boolean) => {
    assert(!!planTypes, "planTypes is undefined");
    assert(!!selectedSubscriptionType, "selectedSubscriptionType is undefined");

    if (isFree) {
      await cancelSubscription();
      return;
    }

    const checkout = await createCheckout({
      planType: plan.planType,
      subscriptionType: selectedSubscriptionType.id,
    });

    window.location.href = checkout.value.data;
  };

  if (!isSuccess || !selectedSubscriptionType) return <LoadingScreen className="bg-purple-100" />;

  const { subscriptionTypes, plans } = data;

  return (
    <PageMainContent scrollable className="bg-purple-100">
      
      <div className="m-auto">
        <FilterTabs
          className="m-auto"
          onChange={(v) => setSelectedSubscriptionTypeId(v)}
          value={selectedSubscriptionTypeId ?? 0}
          tabs={subscriptionTypes.map((subscriptionType) => ({
            label: subscriptionType.name,
            value: subscriptionType.id,
            color: "premium",
          }))}
        />

        <div className="mt-18 flex gap-15.5">
          {plans.map((plan) => (
            <PaymentPlanSummary
              key={plan.title.en}
              {...{
                plan,
                isLoading: isCreatingCheckoutSession || isCancelingSubscription,
                selectedSubscriptionType,
                onSwitchPlan,
              }}
            />
          ))}
        </div>
      </div>
    </PageMainContent>
  );
}
